import React from "react";
import { Button } from "./ui/button";
import { Shield, Lock, Eye, Users, Server, CheckCircle } from "lucide-react";

const DataSecurityDisclaimer = ({ onAccept, userName }) => {
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="security-disclaimer">
      <div className="bg-white border-2 border-black max-w-lg w-full relative" style={{ boxShadow: '6px 6px 0px #000' }}>
        
        {/* Header */}
        <div className="bg-[#050508] text-white p-6 pb-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#0047AB] flex items-center justify-center">
              <Shield size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-black font-['Chivo'] uppercase tracking-wide">Data Security Notice</h2>
              <p className="text-xs text-zinc-400 font-mono uppercase tracking-wider">GPI.Contracts - Project Command Center</p>
            </div>
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Welcome{userName ? `, ${userName}` : ''}. Before you proceed, please review our data security commitment.
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          
          {/* Key commitments */}
          <div className="space-y-3">
            {[
              {
                icon: Lock,
                title: "100% Secure Data",
                desc: "All your data is encrypted at rest and in transit using industry-standard AES-256 encryption and TLS 1.3 protocols."
              },
              {
                icon: Eye,
                title: "No Data Sharing Between Users",
                desc: "Your project data, contracts, and documents are never shared with, visible to, or accessible by other users or organisations."
              },
              {
                icon: Users,
                title: "Organisation-Level Privacy",
                desc: "Your data is only visible to you and authorised members within your organisation. Complete data isolation is enforced at every level."
              },
              {
                icon: Server,
                title: "No Third-Party Data Storage",
                desc: "We do not sell, share, or transfer your data to any third parties. Your documents and project information remain exclusively yours."
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 border border-zinc-100 bg-zinc-50/50 hover:bg-zinc-50 transition-colors">
                <div className="w-8 h-8 border border-[#0047AB]/20 bg-[#0047AB]/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <item.icon size={14} className="text-[#0047AB]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-zinc-900">{item.title}</p>
                  <p className="text-xs text-zinc-500 leading-relaxed mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Compliance badges */}
          <div className="flex items-center justify-center gap-4 py-2 border-t border-zinc-100 pt-4">
            {["256-bit SSL", "2FA Authentication", "GDPR Compliant", "SOC 2 Ready"].map((badge, i) => (
              <div key={i} className="flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-wider text-zinc-500">
                <CheckCircle size={10} className="text-emerald-500" />
                {badge}
              </div>
            ))}
          </div>

          {/* Accept button */}
          <Button 
            onClick={onAccept} 
            className="w-full bg-[#0047AB] hover:bg-[#003580] text-white py-4 h-auto text-sm font-bold uppercase tracking-widest"
            data-testid="btn-accept-disclaimer"
          >
            <Shield size={16} className="mr-2" />
            I Understand — Continue to Platform
          </Button>
          
          <p className="text-[10px] text-center text-zinc-400 font-mono">
            By continuing, you acknowledge our data security practices and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DataSecurityDisclaimer;
